import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import purchaseOrdersAPI from '../../services/purchaseOrdersAPI';
import { parseSpreadsheetMlRows } from './OrganizationsBulkImportModal';

const TEMPLATE_HEADERS = [
  'po_code',
  'order_date',
  'supplier_name',
  'supplier_rut',
  'contact_name',
  'buyer_name',
  'document_type',
  'currency',
  'exchange_rate',
  'payment_date',
  'payment_terms',
  'delivery_conditions',
  'delivery_place',
  'comments',
];

const COLUMN_LABELS = {
  po_code: 'O.C',
  order_date: 'Fecha',
  supplier_name: 'Proveedor',
  supplier_rut: 'RUT prov.',
  contact_name: 'Contacto',
  buyer_name: 'Comprador',
  document_type: 'Factura',
  currency: 'Moneda',
  exchange_rate: 'Valor en CLP',
  payment_date: 'Fecha pago',
  payment_terms: 'Condiciones de pago',
  delivery_conditions: 'Condición de envío',
  delivery_place: 'Método de envío',
  comments: 'Obs',
};

const TEMPLATE_EXAMPLE_CELLS = [
  '25300',
  '2026-09-11',
  'ATMOS',
  '77.682.874-2',
  'Cristian Hidalgo',
  'V. COLL',
  'Factura con IVA',
  'CLP',
  '1',
  '2026-09-17',
  'CRÉDITO A 30 DÍAS',
  'Despacho gratis en Talcahuano',
  'Por parte del proveedor',
  '',
];

const HEADER_ALIASES = {
  po_code: ['po_code', 'oc', 'o_c', 'orden', 'orden_de_compra', 'n_oc', 'nro_oc', 'numero_oc', 'codigo'],
  order_date: ['order_date', 'fecha', 'fecha_oc', 'fecha_orden', 'fecha_de_oc'],
  supplier_name: ['supplier_name', 'proveedor', 'nombre_proveedor', 'supplier'],
  supplier_rut: ['supplier_rut', 'rut_prov', 'rut_proveedor', 'rut'],
  contact_name: ['contact_name', 'contacto', 'contact', 'nombre_contacto'],
  buyer_name: ['buyer_name', 'comprador', 'buyer', 'ejecutivo'],
  document_type: ['document_type', 'factura', 'tipo_documento', 'tipo_factura'],
  currency: ['currency', 'moneda'],
  exchange_rate: ['exchange_rate', 'valor_en_clp', 'tipo_cambio', 'cambio', 'tc'],
  payment_date: ['payment_date', 'fecha_pago', 'fecha_de_pago'],
  payment_terms: ['payment_terms', 'condiciones_de_pago', 'condiciones_pago', 'forma_de_pago'],
  delivery_conditions: [
    'delivery_conditions',
    'condicion_de_envio',
    'condiciones_de_envio',
    'condicion_envio',
  ],
  delivery_place: ['delivery_place', 'metodo_de_envio', 'metodo_envio', 'lugar_entrega'],
  comments: ['comments', 'obs', 'observaciones', 'notas', 'notes', 'comentario'],
};

const emptyRow = () =>
  TEMPLATE_HEADERS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const downloadPurchaseOrdersExcelTemplate = () => {
  const headerCells = TEMPLATE_HEADERS.map(
    (header) => `<Cell><Data ss:Type="String">${escapeXml(COLUMN_LABELS[header] || header)}</Data></Cell>`
  ).join('');
  const exampleCells = TEMPLATE_EXAMPLE_CELLS.map(
    (cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
  ).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Ordenes de compra">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_ordenes_compra.xls';
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const mapHeaderToField = (header) => {
  const key = normalizeHeader(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(key)) return field;
  }
  return TEMPLATE_HEADERS.includes(key) ? key : null;
};

const looksLikeSpreadsheetMl = (text = '') => {
  const sample = String(text || '').slice(0, 4000).toLowerCase();
  return (
    sample.includes('mso-application') ||
    sample.includes('urn:schemas-microsoft-com:office:spreadsheet') ||
    (sample.includes('<workbook') && sample.includes('<worksheet'))
  );
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '');
  if (!sample) return false;
  if (sample.charCodeAt(0) === 0x50 && sample.charCodeAt(1) === 0x4b) return true;
  const nulls = [...sample.slice(0, 2000)].filter((ch) => ch.charCodeAt(0) === 0).length;
  return nulls > 20;
};

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis >= commas) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const buildRowsFromCellTable = (table = []) => {
  if (!table.length) return { rows: [], error: 'No se encontraron filas.' };
  const mapping = (table[0] || []).map(mapHeaderToField);
  if (!mapping.includes('po_code')) {
    return { rows: [], error: 'Falta la columna de O.C. (código de orden de compra).' };
  }
  const rows = [];
  for (let i = 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    const row = emptyRow();
    mapping.forEach((field, idx) => {
      if (!field) return;
      row[field] = String(cells[idx] ?? '').trim();
    });
    if (row.po_code) rows.push(row);
  }
  if (!rows.length) return { rows: [], error: 'No se encontraron filas con código de O.C.' };
  return { rows, error: '' };
};

export const parsePurchaseOrdersCsv = (rawText = '') => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV / Excel.' };
  if (looksLikeBinaryOffice(text)) {
    return { rows: [], error: 'xlsx' };
  }
  if (looksLikeSpreadsheetMl(text)) {
    return buildRowsFromCellTable(parseSpreadsheetMlRows(text));
  }
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], error: 'El archivo está vacío.' };
  const delimiter = detectDelimiter(lines[0]);
  const table = lines.map((line) => splitCsvLine(line, delimiter));
  return buildRowsFromCellTable(table);
};

const foldName = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');

const foldRut = (value = '') =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9K]/g, '');

const PAYMENT_ALIASES = {
  alcontado: 'Contado',
  contado: 'Contado',
  pagoweb: 'Contado',
  contraentrega: 'Contado',
  creditoa15dias: 'Crédito 15 días',
  credito15dias: 'Crédito 15 días',
  creditoa30dias: 'Crédito 30 días',
  credito30dias: 'Crédito 30 días',
  creditoa45dias: 'Crédito 45 días',
  credito45dias: 'Crédito 45 días',
  creditoa60dias: 'Crédito 60 días',
  credito60dias: 'Crédito 60 días',
  creditoa90dias: 'Crédito 90 días',
  credito90dias: 'Crédito 90 días',
  '50contraoc': '50% contra O.C',
  mixto: 'Mixto',
};

const DOCUMENT_ALIASES = {
  facturaconiva: 'Factura afecta',
  facturaafecta: 'Factura afecta',
  facturaexenta: 'Factura exenta',
  guiadedespacho: 'Guía de despacho',
  guia: 'Guía de despacho',
  boleta: 'Boleta',
};

const pickCatalogValue = (raw, options = [], aliases = {}) => {
  const value = String(raw || '').trim();
  const folded = foldName(value);
  if (!folded || folded === '0' || folded === 'na' || folded === 'nd') return '';
  const labels = (options || []).map((opt) => opt.label || opt.value || opt).filter(Boolean);
  const hit = labels.find((label) => foldName(label) === folded);
  if (hit) return hit;
  const mapped = aliases[folded];
  if (mapped) {
    const catalogHit = labels.find((label) => foldName(label) === foldName(mapped));
    return catalogHit || mapped;
  }
  return value;
};

const matchSupplierFromMaster = (row, suppliers = []) => {
  const nameKey = foldName(row.supplier_name);
  const rutKey = foldRut(row.supplier_rut);
  const nameHits = (suppliers || []).filter(
    (supplier) =>
      (nameKey && (foldName(supplier.name) === nameKey || foldName(supplier.business_name) === nameKey))
  );
  const unique = [];
  const seen = new Set();
  nameHits.forEach((supplier) => {
    if (seen.has(supplier.id)) return;
    seen.add(supplier.id);
    unique.push(supplier);
  });
  const rutHit = rutKey
    ? (suppliers || []).find((supplier) => foldRut(supplier.rut) === rutKey)
    : null;
  if (unique.length === 1) return unique[0];
  if (unique.length > 1) {
    if (rutHit && unique.some((supplier) => supplier.id === rutHit.id)) return rutHit;
    return null;
  }
  return rutHit || null;
};

const homologateImportRow = (row, { suppliers = [], contacts = [], paymentTermOptions = [], documentTypeOptions = [] } = {}) => {
  const next = { ...row };
  const supplier = matchSupplierFromMaster(next, suppliers);
  if (supplier) {
    next.supplier_name = supplier.name || next.supplier_name;
    next.supplier_rut = supplier.rut || next.supplier_rut;
    const wantedContact = foldName(next.contact_name);
    if (wantedContact) {
      const contact = (contacts || []).find(
        (item) =>
          String(item.supplier_id) === String(supplier.id) && foldName(item.full_name) === wantedContact
      );
      if (contact) next.contact_name = contact.full_name;
    }
  }
  next.payment_terms = pickCatalogValue(next.payment_terms, paymentTermOptions, PAYMENT_ALIASES);
  next.document_type = pickCatalogValue(next.document_type, documentTypeOptions, DOCUMENT_ALIASES);
  return next;
};

const getRowErrors = (row, suppliers = []) => {
  const errors = {};
  if (!String(row.po_code || '').trim()) errors.po_code = 'Obligatorio';
  if (!String(row.supplier_name || '').trim() && !String(row.supplier_rut || '').trim()) {
    errors.supplier_name = 'Proveedor o RUT';
    errors.supplier_rut = 'Proveedor o RUT';
  } else if (!matchSupplierFromMaster(row, suppliers)) {
    errors.supplier_name = 'No está en el maestro de proveedores';
  }
  return errors;
};

const toApiPayload = (row) => ({
  po_code: String(row.po_code || '').trim(),
  order_date: String(row.order_date || '').trim() || null,
  supplier_name: String(row.supplier_name || '').trim(),
  supplier_rut: String(row.supplier_rut || '').trim(),
  contact_name: String(row.contact_name || '').trim(),
  buyer_name: String(row.buyer_name || '').trim(),
  document_type: String(row.document_type || '').trim(),
  currency: String(row.currency || 'CLP').trim() || 'CLP',
  exchange_rate: String(row.exchange_rate || '').trim() || null,
  payment_date: String(row.payment_date || '').trim() || null,
  payment_terms: String(row.payment_terms || '').trim(),
  delivery_conditions: String(row.delivery_conditions || '').trim(),
  delivery_place: String(row.delivery_place || '').trim(),
  comments: String(row.comments || '').trim(),
  status: 'Borrador',
});

const PurchaseOrdersBulkImportModal = ({
  isOpen = false,
  onClose,
  onImport,
  isImporting = false,
  isDarkMode = false,
  result = null,
  suppliers = [],
  contacts = [],
  paymentTermOptions = [],
  documentTypeOptions = [],
}) => {
  const [step, setStep] = useState('source');
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isParsingFile, setIsParsingFile] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('source');
    setRawText('');
    setPreviewRows([]);
    setLocalError('');
    setLocalSuccess('');
    setIsParsingFile(false);
  }, [isOpen]);

  const colors = {
    text: isDarkMode ? '#f4f4f5' : '#0f172a',
    muted: isDarkMode ? '#a1a1aa' : '#64748b',
    border: isDarkMode ? '#3f3f46' : '#e2e8f0',
    inputBg: isDarkMode ? '#18181b' : '#fff',
    inputBorder: isDarkMode ? '#3f3f46' : '#cbd5e1',
    panel: isDarkMode ? '#09090b' : '#fff',
  };

  const parsed = useMemo(() => parsePurchaseOrdersCsv(rawText), [rawText]);

  const rowErrors = useMemo(
    () => previewRows.map((row) => getRowErrors(row, suppliers)),
    [previewRows, suppliers]
  );
  const invalidFieldCount = rowErrors.reduce((sum, err) => sum + Object.keys(err).length, 0);

  if (!isOpen) return null;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setLocalError('');
    setLocalSuccess('');
    const name = String(file.name || '').toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xlsm') || file.type.includes('spreadsheetml');
    try {
      if (isXlsx) {
        setIsParsingFile(true);
        const data = await purchaseOrdersAPI.parseBulkFile(file);
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!rows.length) {
          setLocalError('No se encontraron filas con código de O.C.');
          return;
        }
        setPreviewRows(
          rows.map((row) =>
            homologateImportRow(
              { ...emptyRow(), ...row },
              { suppliers, contacts, paymentTermOptions, documentTypeOptions }
            )
          )
        );
        setStep('preview');
        return;
      }
      const text = await file.text();
      if (looksLikeBinaryOffice(text)) {
        setIsParsingFile(true);
        const data = await purchaseOrdersAPI.parseBulkFile(file);
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!rows.length) {
          setLocalError('No se encontraron filas con código de O.C.');
          return;
        }
        setPreviewRows(
          rows.map((row) =>
            homologateImportRow(
              { ...emptyRow(), ...row },
              { suppliers, contacts, paymentTermOptions, documentTypeOptions }
            )
          )
        );
        setStep('preview');
        return;
      }
      setRawText(text);
    } catch (err) {
      setLocalError(err?.message || 'No se pudo leer el archivo.');
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleContinueFromSource = () => {
    if (parsed.error || !parsed.rows.length) {
      setLocalError(parsed.error || 'No hay filas para revisar.');
      return;
    }
    setPreviewRows(
      parsed.rows.map((row) =>
        homologateImportRow(
          { ...emptyRow(), ...row },
          { suppliers, contacts, paymentTermOptions, documentTypeOptions }
        )
      )
    );
    setStep('preview');
    setLocalError('');
    setLocalSuccess('');
  };

  const updatePreviewCell = (index, key, value) => {
    setPreviewRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const removePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!previewRows.length || invalidFieldCount > 0 || isImporting || localSuccess) return;
    setLocalError('');
    try {
      const data = await onImport?.(previewRows.map(toApiPayload));
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setLocalSuccess(
          `Importación de cabeceras: ${created} OC creadas` +
            (skipped ? `, ${skipped} omitidas` : '') +
            (Number(data?.payment_terms_assigned || 0)
              ? `, ${data.payment_terms_assigned} condición(es) de pago asignadas a proveedores`
              : '') +
            '.'
        );
      }
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !isImporting && !isParsingFile && onClose?.()}
    >
      <div
        style={{
          width: 'min(1180px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 16,
          display: 'grid',
          gap: 12,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
              {step === 'preview' ? 'Revisar importación' : 'Carga masiva de órdenes de compra'}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>
              {step === 'preview'
                ? 'El proveedor debe existir en el maestro. Contacto y RUT se homologan a ese proveedor; las condiciones de pago se le asignan.'
                : 'Sube el Excel de cabeceras. Solo se importan OC de proveedores ya creados; no se crean proveedores ni contactos.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting || isParsingFile}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.muted,
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isParsingFile ? 'wait' : 'pointer',
                }}
              >
                <Upload size={15} />
                {isParsingFile ? 'Leyendo archivo…' : 'Subir Excel / CSV'}
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx,.xlsm,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  disabled={isParsingFile}
                  onChange={handleFile}
                />
              </label>
              <button
                type="button"
                onClick={downloadPurchaseOrdersExcelTemplate}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Descargar plantilla Excel
              </button>
            </div>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                setLocalError('');
              }}
              placeholder={`O.C;FECHA;PROVEEDOR;RUT PROV.;CONTACTO;...\n${TEMPLATE_EXAMPLE_CELLS.join(';')}`}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 12, color: colors.muted }}>
              {isParsingFile
                ? 'Procesando el Excel…'
                : parsed.error === 'xlsx'
                  ? 'Usa el botón Subir para archivos .xlsx.'
                  : parsed.error
                    ? parsed.error
                    : `${parsed.rows.length} fila(s) detectadas. Continúa para revisarlas.`}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                fontSize: 12,
                color: colors.muted,
              }}
            >
              <span>
                {previewRows.length} fila(s)
                {invalidFieldCount > 0
                  ? ` · ${invalidFieldCount} campo(s) con error`
                  : ' · formato OK'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setStep('source');
                  setLocalError('');
                }}
                disabled={isImporting}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.text,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                }}
              >
                Volver al archivo
              </button>
            </div>
            <div
              style={{
                overflow: 'auto',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                maxHeight: '58vh',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1280 }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>#</th>
                    {TEMPLATE_HEADERS.map((key) => (
                      <th key={key} style={{ padding: '8px 10px', position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>
                        {COLUMN_LABELS[key]}
                      </th>
                    ))}
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }} />
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    return (
                      <tr key={`row-${index}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '6px 10px', color: colors.muted }}>{index + 1}</td>
                        {TEMPLATE_HEADERS.map((key) => {
                          const fieldError = errors[key];
                          return (
                            <td key={key} style={{ padding: '4px 6px', verticalAlign: 'top' }}>
                              <input
                                value={row[key] ?? ''}
                                onChange={(e) => updatePreviewCell(index, key, e.target.value)}
                                aria-invalid={Boolean(fieldError)}
                                style={{
                                  width: key === 'comments' || key === 'delivery_conditions' ? 140 : 108,
                                  height: 30,
                                  borderRadius: 6,
                                  border: `1px solid ${fieldError ? '#dc2626' : colors.inputBorder}`,
                                  background: fieldError
                                    ? isDarkMode
                                      ? '#450a0a'
                                      : '#fef2f2'
                                    : colors.inputBg,
                                  color: fieldError ? (isDarkMode ? '#fecaca' : '#991b1b') : colors.text,
                                  padding: '0 8px',
                                  fontSize: 12,
                                  boxSizing: 'border-box',
                                  fontWeight: fieldError ? 600 : 400,
                                }}
                                title={fieldError || ''}
                              />
                            </td>
                          );
                        })}
                        <td style={{ padding: '4px 8px' }}>
                          <button
                            type="button"
                            onClick={() => removePreviewRow(index)}
                            disabled={isImporting}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.muted,
                              cursor: 'pointer',
                              padding: 4,
                              display: 'inline-flex',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {(localError || localSuccess || result) && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              color: colors.text,
              background: isDarkMode ? '#09090b' : '#f8fafc',
            }}
          >
            {localSuccess ? (
              <div style={{ color: isDarkMode ? '#86efac' : '#15803d', fontWeight: 600 }}>
                {localSuccess}
              </div>
            ) : null}
            {localError ? <div style={{ color: '#dc2626' }}>{localError}</div> : null}
            {!localSuccess && result ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <div>
                  Creadas: <strong>{result.created || 0}</strong> · Omitidas:{' '}
                  <strong>{result.skipped || 0}</strong> · Errores:{' '}
                  <strong>{result.errors || 0}</strong>
                </div>
                {Array.isArray(result.error_rows) && result.error_rows.length > 0 ? (
                  <div style={{ color: '#dc2626', fontSize: 12 }}>
                    {result.error_rows.slice(0, 6).map((row) => (
                      <div key={`err-${row.row}`}>
                        Fila {row.row}{row.po_code ? ` (${row.po_code})` : ''}: {row.detail}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting || isParsingFile}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.inputBorder}`,
              background: 'transparent',
              color: colors.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {step === 'source' ? (
            <button
              type="button"
              onClick={handleContinueFromSource}
              disabled={Boolean(parsed.error) || !parsed.rows.length || isParsingFile}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: !parsed.rows.length || parsed.error || isParsingFile ? '#94a3b8' : '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: !parsed.rows.length || parsed.error || isParsingFile ? 'not-allowed' : 'pointer',
              }}
            >
              Revisar datos
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isImporting || Boolean(localSuccess) || invalidFieldCount > 0 || !previewRows.length}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? '#86efac'
                    : '#16a34a',
                color: '#fff',
                fontWeight: 600,
                cursor:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isImporting ? 'Importando…' : localSuccess ? 'Listo' : `Importar ${previewRows.length || ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersBulkImportModal;
